import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import type { User, Organization } from '@/types';

interface AuthState {
  creditMessage: string;
  user: User | Record<string, never>;
  organization: Organization | Record<string, never>;
  token: string;
  isAuthenticated: boolean;
  isPilot: boolean;
  isAdmin: boolean;

  setCreditMessage: (creditMessage: string) => void;
  updateUser: (data: User) => void;
  updateOrganization: (data: Organization) => void;
  updateToken: (token: string) => void;
  authenticate: () => void;
  logout: () => void;
}

const initialState = {
  creditMessage: '',
  user: {} as User | Record<string, never>,
  organization: {} as Organization | Record<string, never>,
  token: '',
  isAuthenticated: false,
  isPilot: false,
  isAdmin: false,
};

const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setCreditMessage: (creditMessage) => set({ creditMessage }),
        updateUser: (data) =>
          set({
            user: { ...data },
            isAdmin: data.admin ?? false,
          }),

        updateOrganization: (data) =>
          set({
            organization: { ...data },
            isPilot: data.pilot ?? false,
          }),
        updateToken: (token) => set({ token }),
        authenticate: () => set({ isAuthenticated: true }),

        logout: () => {
          // Clear Zustand state
          set({ ...initialState });

          // Clear all localStorage (including Zustand persisted stores)
          const localKeysToRemove = [
            // Direct keys
            'authToken',
            'currentPage',
            'hasViewedTemporaryChatModal',
            'showDataPrivacy',
            'slack_team_id',
            // Zustand persisted stores
            'auth-store',
            'chats',
            'polling-store',
            'InsightsStore',
            'ProjectsStore',
            'ProjectsStoreNew',
            'IntegrationsStore',
            'PermissionsStore',
            'EnumsStore',
            'TeamsStore',
            'ActionStore',
          ];

          // Clear all sessionStorage
          const sessionKeysToRemove = ['chats', 'sidebarCollapsed'];

          // Remove all specified keys
          localKeysToRemove.forEach((key) => localStorage.removeItem(key));
          sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

          // Complete wipe: clear entire localStorage and sessionStorage as fallback
          // This ensures no stale data remains even if we missed a key
          // localStorage.clear();
          // sessionStorage.clear();

          // Redirect
          window.location.href = '/login';
        },
      }),
      {
        name: 'auth-store',
        storage: createJSONStorage(() => localStorage),
      }
    ),
    { name: 'AuthStore' }
  )
);

export default useAuthStore;
